/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import {
  BlockUI,
  FileType,
  getDiagnosticReportOptions,
  PortalCard,
  ExamCard,
  IStudyOrderResponse,
} from "@advapacs/common";
import React, { FC, useEffect, useState } from "react";
import { IRootState } from "@src/config/reducers";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@src/config/store";
import { getExamsListing, studyDownloadPdfReport } from "./ducks/operators";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SortAsc from "@src/assets/icons/sort-asc.svg";
import SortDesc from "@src/assets/icons/sort-desc.svg";
import ReportIcon from "@src/assets/icons/rp-icon.svg";
import ImageIcon from "@src/assets/icons/imagesmode.svg";
import DownloadIcon from "@src/assets/icons/download.svg";
import InfoIcon from "@src/assets/icons/ic-information.svg";
import ClockIcon from "@src/assets/icons/ic-clock.svg";
import WarningNotAvailableIcon from "@src/assets/icons/warning-icon-not-available.svg";
import { ExamActions } from "./ducks/slices";
import { studyOrderAPI } from "@src/config/api";
import { NoResult } from "@src/components/NoResult";
import { LoginAction } from "@src/reducers/slices/loginSlice";

const ExamsContainer: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isMobile, isMobileOnly } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const { studies, loading } = useSelector(
    (state: IRootState) => state.examsReducer
  );
  const { account } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );
  const { eulaDetail } = useSelector(
    (state: IRootState) => state.userLicenseReducer
  );

  const [desc, setDesc] = useState(true);

  useEffect(() => {
    // WILL HANDLE LAZY LOADING LATER.
    dispatch(getExamsListing({ page: 0, size: 1000 }));
    return () => {
      dispatch(ExamActions.clearData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if user not accept eula need to return eula modal.
    if (eulaDetail?.eula?.id) {
      dispatch(LoginAction.setIsShowEulaAfterLogin(true));
      navigate("/terms-of-service");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eulaDetail]);

  const handleDownloadReport = async (item: IStudyOrderResponse) => {
    try {
      const response = await axios.get(
        studyOrderAPI.GET_STUDY_ORDER_DIAGNOSTIC_REPORT(item.id)
      );
      const reportResponse = response.data;
      const report = getDiagnosticReportOptions(reportResponse);
      const contentOptions = report.options;

      const hasGenerateReport = report.isGeneratePdfReport;

      if (contentOptions.length === 0) return;
      studyDownloadPdfReport(
        item.id,
        contentOptions?.find((x) => x.label === FileType.PDF)?.value?.id,
        hasGenerateReport
      );
    } catch (error) {}
  };

  return (
    <div>
      <h4 className="m-b-sm flexbox flex-center-between font-weight">
        All Exams ({studies.length})
        <button
          className="btn btn-light btn-l-sort"
          onClick={() => {
            setDesc(!desc);
            const isDesc = !desc;
            dispatch(
              getExamsListing({
                page: 0,
                size: 1000,
                sort: isDesc ? "studyDate,desc" : "studyDate,asc",
              })
            );
          }}
        >
          {!desc && <img className="" src={SortDesc} alt="Sort Icon" />}
          {desc && <img className="" src={SortAsc} alt="Sort Icon" />}
        </button>
      </h4>
      <BlockUI isBlocked={loading}>
        <div className="study-listing">
          {(studies || []).map((std, index) => {
            return (
              <PortalCard
                key={index}
                wrapperClass={`study-card ${
                  index !== studies?.length - 1 ? "m-b-xs" : ""
                }`}
              >
                <ExamCard
                  dateFormat={account?.localisation?.dateTimeFormat}
                  plainDateFormat={account?.localisation?.dateFormat}
                  infoIcon={
                    <img src={InfoIcon} className="m-r-xxs" alt="Info Icon" />
                  }
                  clockIcon={
                    <img src={ClockIcon} className="m-r-xxs" alt="Clock Icon" />
                  }
                  warnIcon={
                    <img
                      src={WarningNotAvailableIcon}
                      className="m-r-xxs"
                      alt="Warn Icon"
                    />
                  }
                  downloadIcon={DownloadIcon}
                  reportIcon={ReportIcon}
                  imageIcon={ImageIcon}
                  onNavigate={(type) => {
                    navigate(`/Exams/Studies/${std.id}?type=${type}`);
                  }}
                  isMobile={isMobile}
                  isMobileOnly={isMobileOnly}
                  study={std as IStudyOrderResponse}
                  download={() => {
                    handleDownloadReport(std);
                  }}
                />
              </PortalCard>
            );
          })}

          {studies?.length === 0 && <NoResult type="Exams" />}
        </div>
      </BlockUI>
    </div>
  );
};

export default ExamsContainer;
